<template>
  <div class="rso-node-wrap">
    <div
      class="rso-node-row"
      :class="[
        'rso-row',
        `rso-row--${node.node_type}`,
        (depth || 0) === 0 ? 'rso-row--root' : '',
      ]"
    >
      <!-- Та же colgroup, что у шапки — итог строго в последней колонке -->
      <table
        class="rso-node-row-table rso-mini-table rso-smeta-grid-table"
        lang="ru"
      >
        <colgroup>
          <col
            v-for="c in RESIDENT_SMETA_GRID_COLS"
            :key="`nc-${c.key}`"
            :style="{ width: c.width }"
          />
        </colgroup>
        <tbody>
          <tr class="rso-node-tr">
            <template v-if="node.node_type === 'work'">
              <td
                class="rso-node-row-td rso-node-row-td--title tw-text-left"
                :style="{ paddingLeft: `${treeCellPadPx}px` }"
              >
                <div class="rso-node-row-label-inner rso-node-row-label-inner--work">
                  <span class="rso-work-align-spacer" />
                  <span class="rso-node-label rso-node-label--work">{{ node.label }}</span>
                </div>
              </td>
              <td class="rso-node-row-td rso-node-row-td--data tw-text-left">{{ node.wunt || "—" }}</td>
              <td class="rso-node-row-td rso-node-row-td--data rso-cell--num tw-text-right">{{ formatNum(node.wqty) }}</td>
              <td class="rso-node-row-td rso-node-row-td--data rso-cell--num tw-text-right"><span class="rso-empty">—</span></td>
              <td class="rso-node-row-td rso-node-row-td--data rso-cell--num tw-text-right">
                <Amount
                  v-if="hasCostAmount(node.wprc_no_nds)"
                  :value="node.wprc_no_nds"
                  :round="2"
                />
                <span v-else class="rso-empty">—</span>
              </td>
            </template>
            <template v-else>
              <td
                class="rso-node-row-td rso-node-row-td--label tw-text-left"
                :colspan="treeLabelColspan"
                :style="{ paddingLeft: `${treeCellPadPx}px` }"
              >
                <div class="rso-node-row-label-inner">
                <button
                  v-if="canExpand"
                  type="button"
                  class="rso-expand-btn"
                  :aria-expanded="isOpen ? 'true' : 'false'"
                  @click="emit('toggle', node.id)"
                >
                  <span class="rso-expand-icon">{{ isOpen ? "▾" : "▸" }}</span>
                </button>
                <span v-else class="rso-expand-spacer" />

                <q-icon
                  v-if="node.node_type === 'resident'"
                  name="apartment"
                  size="13px"
                  class="rso-icon rso-icon--resident"
                />
                <q-icon
                  v-else-if="node.node_type === 'usp' || node.node_type === 'usp_work'"
                  name="account_tree"
                  size="13px"
                  class="rso-icon rso-icon--usp"
                />
                <q-icon
                  v-else-if="node.node_type === 'unmapped' || node.node_type === 'unmapped_reason'"
                  name="link_off"
                  size="13px"
                  class="rso-icon rso-icon--unmapped"
                />

                <div
                  v-if="node.node_type === 'block' && node.projectIdLine"
                  class="rso-block-label-stack"
                >
                  <span class="rso-node-label rso-node-label--block">
                    {{ node.label }}
                  </span>
                  <span class="rso-node-label-sub rso-node-label-sub--project-id">
                    ID проекта:
                    <span class="rso-node-label-sub-value">{{ node.project_id }}</span>
                  </span>
                </div>
                <span
                  v-else
                  class="rso-node-label"
                  :class="{
                    'rso-node-label--root': node.node_type === 'resident',
                    'rso-node-label--project_type': node.node_type === 'project_type',
                    'rso-node-label--block': node.node_type === 'block',
                    'rso-node-label--floor': node.node_type === 'floor',
                    'rso-node-label--group': node.node_type === 'group',
                    'rso-node-label--usp': node.node_type === 'usp' || node.node_type === 'usp_work',
                    'rso-node-label--unmapped': node.node_type === 'unmapped' || node.node_type === 'unmapped_reason',
                    'rso-node-label--materials': node.node_type === 'materials',
                  }"
                >
                  {{ node.label }}
                </span>
              </div>
              </td>
            </template>
            <td class="rso-node-row-td rso-node-row-td--cost tw-text-right">
              <Amount
                v-if="hasCostAmount(node.wsum_cost)"
                :value="node.wsum_cost"
                :round="2"
              />
              <span v-else class="rso-empty">—</span>
            </td>
            <td class="rso-node-row-td rso-node-row-td--cost tw-text-right">
              <Amount
                v-if="hasCostAmount(node.msum_cost)"
                :value="node.msum_cost"
                :round="2"
              />
              <span v-else class="rso-empty">—</span>
            </td>
            <td class="rso-node-row-td rso-node-row-td--cost rso-node-row-td--total tw-text-right">
              <Amount
                v-if="hasCostAmount(node.total_cost)"
                :value="node.total_cost"
                :round="2"
              />
              <span v-else class="rso-empty">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ResidentSmetaMaterialsTable
      v-if="node.node_type === 'materials' && isOpen && materialLines.length"
      :rows="materialLines"
      :node-id="node.id"
      :title-indent-px="materialTitleIndentPx"
    />

    <template v-if="showStructuralChildren">
      <ResidentSmetaTreeNode
        v-for="ch in node.children"
        :key="ch.id"
        :node="ch"
        :depth="(depth || 0) + 1"
        :expanded-ids="expandedIds"
        @toggle="emit('toggle', $event)"
      />
    </template>
  </div>
</template>

<script setup>
import { computed } from "vue";
import Amount from "@/components/common/Amount.vue";
import ResidentSmetaMaterialsTable from "./ResidentSmetaMaterialsTable.vue";
import ResidentSmetaTreeNode from "./ResidentSmetaTreeNode.vue";
import { RESIDENT_SMETA_GRID_COLS } from "../residentSmetaGridColumns.js";

const props = defineProps({
  node: {
    type: Object,
    required: true,
  },
  depth: {
    type: Number,
    default: 0,
  },
  expandedIds: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(["toggle"]);

const TREE_STEP_PX = 10;
const TREE_BASE_PAD_PX = 4;
const EXPAND_BTN_PX = 18;

const treeCellPadPx = computed(
  () => (props.depth || 0) * TREE_STEP_PX + TREE_BASE_PAD_PX
);

/** Колонки до «Стоимость СМР» — подпись дерева (title…price). */
const treeLabelColspan =
  RESIDENT_SMETA_GRID_COLS.findIndex((c) => c.key === "wsum") || 5;

const isOpen = computed(() => props.expandedIds.has(props.node.id));

const hasStructuralChildren = computed(
  () => Array.isArray(props.node.children) && props.node.children.length > 0
);

const materialLines = computed(() => {
  if (props.node.node_type !== "materials") return [];
  return Array.isArray(props.node.materialRows) ? props.node.materialRows : [];
});

/** Отступ только текста названия материала — под правым краем подписи папки «Материалы». */
const materialTitleIndentPx = computed(() => {
  const afterFolderControls = EXPAND_BTN_PX + 4 + 12 + 4 + 8;
  return treeCellPadPx.value + afterFolderControls;
});

/** Папка «Материалы» под работой — всегда; остальные уровни — при раскрытии. */
const showStructuralChildren = computed(() => {
  if (!hasStructuralChildren.value) return false;
  if (props.node.node_type === "work") return true;
  return isOpen.value;
});

const canExpand = computed(() => {
  if (props.node.node_type === "work") return hasStructuralChildren.value;
  if (hasStructuralChildren.value) return true;
  if (props.node.node_type === "materials" && materialLines.value.length > 0)
    return true;
  return false;
});

function formatNum(v) {
  if (v == null || v === "") return "—";
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return String(n);
}

function hasCostAmount(v) {
  if (v == null || v === "") return false;
  const n = Number(v);
  return !Number.isNaN(n);
}
</script>

<style scoped lang="scss">
.rso-node-wrap {
  width: 100%;
}

.rso-node-row {
  border-bottom: 1px solid #b8cfe4;
  transition: background-color 0.12s ease;
}

.rso-node-row:hover .rso-node-row-table {
  background-color: #f8fafc;
}

.rso-node-row-table {
  width: 100%;
}

.rso-node-row-td--label {
  vertical-align: middle;
}

.rso-node-row-label-inner {
  display: flex;
  align-items: center;
  gap: 4px;
  min-height: 24px;
  padding: 2px 0;
}

.rso-node-row-td--cost {
  vertical-align: middle;
}

/* Уровень 1 — ЖК */
.rso-row--resident .rso-node-row-table {
  background: #e9f1fb;
}

.rso-row--resident .rso-node-row-td--label {
  border-left: 4px solid #5b8fce;
}

/* Строки дерева — белый фон */
.rso-row--project_type .rso-node-row-table,
.rso-row--group .rso-node-row-table,
.rso-row--block .rso-node-row-table,
.rso-row--floor .rso-node-row-table,
.rso-row--usp .rso-node-row-table,
.rso-row--usp_work .rso-node-row-table,
.rso-row--unmapped .rso-node-row-table,
.rso-row--unmapped_reason .rso-node-row-table,
.rso-row--work .rso-node-row-table {
  background: #fff;
}

.rso-row--project_type .rso-node-row-td--label,
.rso-row--group .rso-node-row-td--label,
.rso-row--usp .rso-node-row-td--label,
.rso-row--usp_work .rso-node-row-td--label {
  border-left: 3px solid #64748b;
}

.rso-row--unmapped .rso-node-row-td--label,
.rso-row--unmapped_reason .rso-node-row-td--label {
  border-left: 3px solid #c2410c;
}

.rso-row--block .rso-node-row-td--label,
.rso-row--floor .rso-node-row-td--label {
  border-left: 4px solid #64748b;
}

/* ЖК и папка «Материалы» — как строка ЖК */
.rso-row--materials .rso-node-row-table {
  background: #e9f1fb;
}

.rso-row--materials .rso-node-row-td--label {
  border-left: 4px solid #5b8fce;
}

.rso-row--materials:hover .rso-node-row-table {
  background: #e9f1fb;
}

.rso-node-row-label-inner--work {
  align-items: flex-start;
}

.rso-work-align-spacer {
  display: inline-block;
  width: 10px;
  flex-shrink: 0;
}

.rso-node-label--work {
  flex: 1;
  min-width: 0;
  font-weight: 400;
  font-size: 12px;
  color: #0f172a;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
  line-height: 1.4;
}

.rso-node-row-td--title {
  vertical-align: middle;
}


.rso-icon {
  flex-shrink: 0;
}

.rso-node-row-td--data {
  font-size: 12px;
  color: #0f172a;
}

.rso-cell--num {
  font-variant-numeric: tabular-nums;
}

.rso-node-row-td--cost {
  background: #fff;
  font-variant-numeric: tabular-nums;
  font-weight: 500;
  color: #0f172a;
}

.rso-row--resident .rso-node-row-td--cost,
.rso-row--materials .rso-node-row-td--cost {
  background: #e9f1fb;
}

.rso-node-row-td--total {
  font-weight: 700;
  color: #0f172a;
}

.rso-empty {
  color: #94a3b8;
}

.rso-expand-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  font-size: 10px;
  border: none;
  border-radius: 4px;
  background: #f1f5f9;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  color: #475569;
  transition: background-color 0.12s ease, color 0.12s ease;
}

.rso-expand-btn:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.rso-expand-spacer {
  display: inline-block;
  width: 18px;
  flex-shrink: 0;
}

.rso-node-label {
  font-size: 12px;
  font-weight: 400;
  color: #0f172a;
  line-height: 1.35;
}

.rso-node-label--root {
  font-weight: 700;
  color: #0f172a;
}

.rso-node-label--project_type,
.rso-node-label--group,
.rso-node-label--block,
.rso-node-label--floor,
.rso-node-label--usp {
  font-weight: 600;
  color: #0f172a;
}

.rso-node-label--unmapped {
  font-weight: 600;
  color: #9a3412;
}

.rso-node-label--materials {
  font-weight: 400;
  font-size: 12px;
  color: #2563eb;
}

.rso-block-label-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.rso-node-label-sub--project-id {
  font-size: 11px;
  font-weight: 400;
  color: #64748b;
  line-height: 1.3;
}

.rso-node-label-sub-value {
  font-weight: 700;
  color: #475569;
}

.rso-td-cost {
  vertical-align: middle;
}
</style>
